import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Users, Building2, ChevronRight, UserPlus, X,
  Briefcase, Layers, MessageSquare, ClipboardList,
  UserCog, Sparkles, Inbox, Tag, KeyRound, AlertTriangle, MessageCircle, Zap,
} from "lucide-react";

function MiniStat({ label, value, accent, sub }) {
  return (
    <div className="p-5 border-r border-b border-border last:border-r-0">
      <div className="font-mono text-[9px] text-muted-foreground tracking-[0.2em] uppercase">{label}</div>
      <div className={`font-display text-3xl font-bold mt-1 ${accent || ""}`}>{value ?? "—"}</div>
      {sub && <div className="font-mono text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function SectionLink({ to, icon: Icon, label, sublabel, badge }) {
  return (
    <Link
      to={to}
      className="relative flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border hover:border-[#0025D8]/40 hover:bg-[#0025D8]/3 transition-all group"
    >
      {badge > 0 && (
        <span className="absolute -top-1.5 -right-1.5 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F59E0B] opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#F59E0B]" />
        </span>
      )}
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-7 h-7 bg-secondary flex items-center justify-center flex-shrink-0 group-hover:bg-[#0025D8]/10 transition-colors">
          <Icon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-[#0025D8] transition-colors" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium group-hover:text-[#0025D8] transition-colors">{label}</div>
          {sublabel && <div className="font-mono text-[10px] text-muted-foreground truncate">{sublabel}</div>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#0025D8] flex-shrink-0 transition-colors" />
    </Link>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newUsers, setNewUsers] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  const [atsDiagUnread, setAtsDiagUnread] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [stats, fb, nu, diag, sup] = await Promise.all([
          api.get("/admin/dashboard-stats"),
          api.get("/admin/feedback/unread-count"),
          api.get("/admin/new-users"),
          // New Workday apply failures since last viewed → badge on the ATS Diagnostics card. Non-critical:
          // never let it break the dashboard load.
          api.get("/admin/ats-diag/unread-count").catch(() => ({ data: { count: 0 } })),
          // Candidates waiting on a reply. Same treatment: never break the dashboard over a badge.
          api.get("/admin/support/unread-count").catch(() => ({ data: { unread_threads: 0 } })),
        ]);
        setData(stats.data);
        setFeedbackUnread(fb.data.count || 0);
        setNewUsers(nu.data);
        setAtsDiagUnread(diag.data.count || 0);
        setSupportUnread(sup.data.unread_threads || 0);
      } catch {
        toast.error("Could not load admin data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading admin…</div>;
  if (!data) return <div className="p-12 font-mono text-sm text-[#E11D48]">Could not load admin data. Check the backend is running.</div>;

  const {
    interviews, completed_interviews: completedInterviews,
    candidates, candidates_basic: candidatesBasic, candidates_premium: candidatesPremium,
    companies, employees, active_employees: activeEmployees,
    roles,
    industries, pending_role_requests: pendingRoleRequests, premium_roles_new: premiumRolesNew,
    resume_analyze: resumeAnalyze, resume_optimize: resumeOptimize,
  } = data;

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      {/* Header */}
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8]">Superuser · Control Room</div>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">Admin</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/feedback" className="relative">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-secondary transition-colors">
              <MessageSquare className="w-4 h-4" />
              Feedback
              {feedbackUnread > 0 && (
                <span className="bg-[#E11D48] text-white text-[10px] font-mono font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
                  {feedbackUnread > 9 ? "9+" : feedbackUnread}
                </span>
              )}
            </button>
          </Link>
          <Link to="/admin/users">
            <button className="flex items-center gap-2 px-4 py-2 bg-[#0025D8] text-white text-sm font-medium hover:bg-[#001CB3] transition-colors">
              <UserCog className="w-4 h-4" />
              All Users →
            </button>
          </Link>
        </div>
      </div>

      {/* New users banner */}
      {newUsers && newUsers.count > 0 && !bannerDismissed && (
        <div className="rounded-xl border border-[#0025D8]/30 bg-[#0025D8]/5 p-4 mb-8 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <UserPlus className="w-4 h-4 text-[#0025D8] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#0025D8]">
                {newUsers.count} new user{newUsers.count !== 1 ? "s" : ""} since your last login
                {newUsers.since && (
                  <span className="font-normal text-muted-foreground ml-1.5 font-mono text-xs">
                    (since {new Date(newUsers.since).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })})
                  </span>
                )}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {newUsers.users.slice(0, 8).map((u) => (
                  <div key={u.user_id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{u.name}</span>
                    <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${
                      u.role === "candidate" ? "bg-[#0025D8]/10 text-[#0025D8]" : "bg-[#7C3AED]/10 text-[#7C3AED]"
                    }`}>
                      {u.role}
                    </span>
                    <span className="text-[10px] font-mono opacity-60">
                      {new Date(u.created_at).toLocaleDateString("en", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                ))}
                {newUsers.count > 8 && (
                  <span className="text-xs text-muted-foreground font-mono">+{newUsers.count - 8} more</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={() => setBannerDismissed(true)} className="text-muted-foreground hover:text-foreground flex-shrink-0 mt-0.5">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Two-section cards */}
      <div className="grid md:grid-cols-2 gap-6">

        {/* ── CANDIDATES card ── */}
        <div className="rounded-xl border border-border bg-white flex flex-col">
          {/* Card header */}
          <div className="px-6 py-4 border-b border-border flex items-center gap-3 bg-[#0025D8]/3">
            <div className="w-8 h-8 bg-[#0025D8]/10 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-[#0025D8]" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-[#0025D8] uppercase font-semibold">Section</div>
              <div className="font-display font-bold text-lg leading-tight">Candidates</div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 border-b border-border">
            <MiniStat
              label="Candidate Users"
              value={candidates}
              sub={`${candidatesBasic} basic · ${candidatesPremium} premium`}
            />
            <MiniStat label="Interviews" value={interviews} />
            <MiniStat label="Completed" value={completedInterviews} accent="text-[#10B981]" />
            <MiniStat label="Resume Analyzed" value={resumeAnalyze} />
            <MiniStat label="Resume Optimized" value={resumeOptimize} />
          </div>

          {/* Quick links */}
          <div className="p-4 space-y-2 flex-1">
            <SectionLink
              to="/admin/candidates"
              icon={ClipboardList}
              label="Interviews"
              sublabel={`${interviews} interviews · ${completedInterviews} completed`}
            />
            <SectionLink
              to="/admin/roles"
              icon={Briefcase}
              label="Interview Roles"
              sublabel={premiumRolesNew > 0
                ? `${roles} roles · ${premiumRolesNew} new from premium users`
                : `${roles} roles configured`}
              badge={premiumRolesNew}
            />
            <SectionLink
              to="/admin/seed"
              icon={Sparkles}
              label="Bulk Seed Catalog"
              sublabel="Auto-create O*NET-grounded roles"
            />
            <SectionLink
              to="/admin/role-requests"
              icon={Inbox}
              label="Role Requests"
              sublabel="Action candidate-requested roles"
              badge={pendingRoleRequests}
            />
            <SectionLink
              to="/admin/industries"
              icon={Layers}
              label="Industries"
              sublabel={`${industries} industries`}
            />
            <SectionLink
              to="/admin/candidate-users"
              icon={UserCog}
              label="Candidate Users"
              sublabel={`${candidates} registered candidates`}
            />
            <SectionLink
              to="/admin/workday-tenants"
              icon={Building2}
              label="Workday Employers"
              sublabel="Auto-discovered employers · review, promote & add by URL"
            />
            <SectionLink
              to="/admin/ats-setups"
              icon={KeyRound}
              label="Account Setup Queue"
              sublabel="Create Dayforce accounts for candidates (reCAPTCHA-gated sign-up)"
            />
            <SectionLink
              to="/admin/ats-diagnostics"
              icon={AlertTriangle}
              label="ATS Apply Diagnostics"
              sublabel="Browser-lane apply failures, clustered by cause — the fix-list"
              badge={atsDiagUnread}
            />
            <SectionLink
              to="/admin/ats-automation"
              icon={Zap}
              label="ATS automation"
              sublabel="Switch one-click apply on or off per ATS — the remedy when one starts failing"
            />
            <SectionLink
              to="/admin/job-supply"
              icon={Layers}
              label="Job supply by source"
              sublabel="Which feed supplies the jobs candidates see — and how much overlaps"
            />
            <SectionLink
              to="/admin/support"
              icon={MessageCircle}
              label="Candidate messages"
              sublabel="Questions and problems candidates have written in about"
              badge={supportUnread}
            />
            <SectionLink
              to="/admin/promo-codes"
              icon={Tag}
              label="Promo Codes"
              sublabel="Create &amp; manage discount codes for sign-up"
            />
          </div>
        </div>

        {/* ── COMPANIES card ── */}
        <div className="rounded-xl border border-border bg-white flex flex-col">
          {/* Card header */}
          <div className="px-6 py-4 border-b border-border flex items-center gap-3 bg-[#7C3AED]/3">
            <div className="w-8 h-8 bg-[#7C3AED]/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-[#7C3AED]" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-[#7C3AED] uppercase font-semibold">Section</div>
              <div className="font-display font-bold text-lg leading-tight">Companies</div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 border-b border-border">
            <MiniStat label="Companies" value={companies} />
            <MiniStat label="Employees" value={employees} />
            <MiniStat label="Active Employees" value={activeEmployees} accent="text-[#10B981]" />
            <MiniStat label="Disabled" value={employees - activeEmployees} accent={(employees - activeEmployees) > 0 ? "text-[#E11D48]" : ""} />
          </div>

          {/* Quick links */}
          <div className="p-4 space-y-2 flex-1">
            <SectionLink
              to="/admin/companies"
              icon={Building2}
              label="Companies & Employees"
              sublabel={`${companies} companies · ${employees} employees · manage all accounts`}
            />
          </div>
        </div>
      </div>
    </main>
  );
}
